import React, { useCallback, useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import freightService from "../../service/freight.service";
import polyline from "@mapbox/polyline";
import axios from "axios";
import "./mapa-comp.css";

/** Base clara (Carto), boa leitura de rotas e ícones — alinhada ao dashboard. */
const TILE_LIGHT =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

const FORTIO_NAVY = "#012443";
const FORTIO_ORANGE = "#FF3100";

/** Tooltip ao passar o mouse: mostra lat/lng (e opcionalmente título / texto extra em HTML). */
function tooltipCoordsOptions(extra = {}) {
  return {
    sticky: true,
    direction: "top",
    opacity: 0.95,
    className: "mapa-comp-coords-tooltip",
    ...extra,
  };
}

/** Converte timestamp Firestore, número (ms) ou string para texto pt-BR. */
function toLocaleDateTimeString(raw) {
  if (raw === undefined || raw === null || raw === "") {
    return null;
  }
  if (typeof raw.toDate === "function") {
    const d = raw.toDate();
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString("pt-BR");
  }
  const d = new Date(typeof raw === "number" ? raw : raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString("pt-BR");
}

function escapeHtml(text) {
  if (text === undefined || text === null) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Bloco padrão: título opcional, data/hora, lat, lng (tooltip e popup).
 */
function htmlLocationDetails({ title, lat, lng, dateTimeStr }) {
  const la = Number(lat);
  const lo = Number(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) {
    return "Coordenadas inválidas";
  }
  const lines = [];
  if (title) {
    lines.push(`<strong>${title}</strong>`);
  }
  lines.push(
    `<strong>Data e hora:</strong> ${dateTimeStr || "—"}`
  );
  lines.push(`<strong>Lat:</strong> ${la.toFixed(6)}`);
  lines.push(`<strong>Lng:</strong> ${lo.toFixed(6)}`);
  return lines.join("<br/>");
}

/**
 * Tooltip/popup do motorista: inclui endereço (geocodificação reversa) quando disponível.
 */
function htmlDriverMarkerContent({ title, lat, lng, dateTimeStr, addressInfo }) {
  let html = htmlLocationDetails({ title, lat, lng, dateTimeStr });
  if (addressInfo) {
    const address = addressInfo.address || "Nao informado";
    const postalCode = addressInfo.postalCode || "Nao informado";
    const street = addressInfo.street || "Nao informado";
    const city = addressInfo.city || "Nao informado";
    html += [
      "<br/><br/><strong>Endereco:</strong> " + escapeHtml(address),
      "<strong>CEP:</strong> " + escapeHtml(postalCode),
      "<strong>Rua:</strong> " + escapeHtml(street),
      "<strong>Cidade:</strong> " + escapeHtml(city),
    ].join("<br/>");
  }
  return html;
}

/**
 * Endereço legível a partir de lat/lng (API pública, CORS no browser).
 * Documentação: BigDataCloud reverse-geocode-client.
 */
async function reverseGeocodeAddress(lat, lng) {
  const la = Number(lat);
  const lo = Number(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) {
    return null;
  }
  try {
    const { data } = await axios.get(
      "https://api.bigdatacloud.net/data/reverse-geocode-client",
      {
        params: {
          latitude: la,
          longitude: lo,
          localityLanguage: "pt",
        },
        timeout: 10000,
      }
    );
    if (!data || typeof data !== "object") return null;

    const streetLine = [data.streetNumber, data.street]
      .filter(Boolean)
      .join(" ")
      .trim();
    const neighborhood = data.neighbourhood || null;
    const city =
      data.city || data.locality || data.localityInfo?.administrative?.[2]?.name || null;
    const state = data.principalSubdivision || null;
    const postalCode = data.postcode || null;
    const country = data.countryName || null;

    const addressParts = [];
    if (streetLine) addressParts.push(streetLine);
    if (neighborhood) addressParts.push(neighborhood);
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postalCode) addressParts.push(postalCode);
    if (country) addressParts.push(country);

    const address = addressParts.filter(Boolean).join(" - ");
    return {
      address: address || null,
      postalCode,
      street: streetLine || data.street || null,
      city,
    };
  } catch (e) {
    console.warn("Geocodificação reversa indisponível:", e?.message || e);
    return null;
  }
}

/**
 * Marcador do motorista: caminhão em SVG (estilo “shipping” / frota) + disco branco.
 * Não depende de arquivo em /public; contraste bom no mapa claro.
 */
function createDriverTruckDivIcon() {
  const html = `
    <div class="mapa-comp-truck-marker" title="Posição do motorista">
      <svg width="44" height="44" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10.5" fill="#ffffff" stroke="${FORTIO_NAVY}" stroke-width="1.5"/>
        <path fill="${FORTIO_ORANGE}" d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    className: "mapa-comp-driver-divicon",
    html,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -20],
  });
}

function removeGpsOverlayLayers(map) {
  map.eachLayer((layer) => {
    const cn = layer.options?.className;
    if (
      cn === "mapa-comp-gps-line" ||
      cn === "mapa-comp-gps-point"
    ) {
      map.removeLayer(layer);
    }
  });
}

/** Em trânsito: só pontos de captura; fora disso, também liga os pontos com polilinha. */
function isFreightInTransit(statusDescribe) {
  if (statusDescribe == null || statusDescribe === "") return false;
  const s = String(statusDescribe).toLowerCase();
  return s.includes("em transito") || s.includes("em trânsito");
}

const MapComponent = ({
  points = [],
  idFreight,
  searchLatestPositions,
  mapHeight = 500,
  /** Ex.: freight.status.describe — usado para esconder a linha reta do GPS durante "Em transito". */
  freightStatusDescribe,
}) => {
  const mapContainerRef = useRef(null);
  const routeHistoryRef = useRef([]);
  /** Evita que um ciclo antigo de redesenho GPS (após await) apague/substitua o desenho de um ciclo mais novo. */
  const gpsOverlayGenRef = useRef(0);
  /** Evita nova chamada de geocodificação se lat/lng não mudou (4 casas). */
  const addressCacheRef = useRef({ key: "", text: null });
  const [map, setMap] = useState(null);
  const [driverMarker, setDriverMarker] = useState(null);
  const [lastPositionReceived, setLastPositionReceived] = useState(null);

  const getCachedOrFetchAddress = useCallback(async (lat, lng) => {
    const key = `${Number(lat).toFixed(4)}|${Number(lng).toFixed(4)}`;
    if (
      addressCacheRef.current.key === key &&
      addressCacheRef.current.text !== undefined
    ) {
      return addressCacheRef.current.text;
    }
    const text = await reverseGeocodeAddress(lat, lng);
    addressCacheRef.current = { key, text };
    return text;
  }, []);

  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;

    const mapInstance = L.map(el, {
      zoomControl: true,
      attributionControl: true,
    }).setView([-23.986178, -46.308402], 13);
    setMap(mapInstance);

    L.tileLayer(TILE_LIGHT, {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 20,
    }).addTo(mapInstance);

    points.forEach((point) => {
      let iconUrl;

      if (point.operation && point.operation.includes("carga")) {
        iconUrl = point.concluded
          ? `${process.env.PUBLIC_URL}/coleta-realizada.png`
          : `${process.env.PUBLIC_URL}/coleta-pendente.png`;
      } else if (point.operation && point.operation.includes("descarga")) {
        iconUrl = point.concluded
          ? `${process.env.PUBLIC_URL}/entrega-realizada.png`
          : `${process.env.PUBLIC_URL}/entrega-pendente.png`;
      } else {
        console.error("Ponto inválido ou sem operação definida:", point);
        return;
      }

      const icon = L.icon({
        iconUrl,
        iconSize: [50, 50],
        iconAnchor: [20, 42],
        popupAnchor: [-3, -32],
      });

      const [lat, lng] = point.coords || [];
      const stopDateTime = toLocaleDateTimeString(
        point.locationTime ?? point.timestamp ?? point.createdAt
      );
      const stopDetails = htmlLocationDetails({
        title: "Ponto de parada",
        lat,
        lng,
        dateTimeStr: stopDateTime,
      });
      const m = L.marker(point.coords, { icon }).addTo(mapInstance);
      m.bindPopup(
        `${point.popupText || "Sem descrição"}<br/><br/>${stopDetails}`
      );
      m.bindTooltip(stopDetails, tooltipCoordsOptions());
    });

    const fetchRoute = async () => {
      try {
        const coordinates = points.map((point) => point?.coords);
        console.log("Pontos enviados para a API de rotas:", coordinates);
        const stoppings_ = await freightService.searchFirstWay(idFreight);
        const startPosition = stoppings_.docs.map((stopping) => ({
          latitude: stopping?.data()?.latitude || 0,
          longitude: stopping?.data()?.longitude || 0,
        }));

        console.log("Posição inicial do motorista:", startPosition);
        if (
          startPosition.length === 0 ||
          !startPosition[0].latitude ||
          !startPosition[0].longitude
        ) {
          console.error("Erro: Posição inicial inválida do motorista");
          return;
        }

        const response = await axios.post(
          "https://8t7wcp4ip0.execute-api.us-east-1.amazonaws.com/dev/directions",
          {
            coordinates:
              startPosition[0].longitude && startPosition[0].latitude
                ? [
                    [startPosition[0].longitude, startPosition[0].latitude],
                    ...coordinates.map((coord) => [coord[1], coord[0]]),
                  ]
                : [...coordinates.map((coord) => [coord[1], coord[0]])],
          }
        );

        console.log("Resposta da API de rotas:", response.data);

        if (response.data.routes && response.data.routes.length > 0) {
          const route = polyline.decode(response.data.routes[0].geometry);

          console.log("Decodificação da rota:", route);

          const polylineLayer = L.polyline(route, {
            color: "#2563eb",
            weight: 5,
            opacity: 0.88,
            lineJoin: "round",
            lineCap: "round",
          }).addTo(mapInstance);
          mapInstance.fitBounds(polylineLayer.getBounds(), { padding: [28, 28] });

          const driverIcon = createDriverTruckDivIcon();

          const initialDriverPosition = [route[0][0], route[0][1]];
          const driverMarkerInstance = L.marker(initialDriverPosition, {
            icon: driverIcon,
            zIndexOffset: 1000,
          }).addTo(mapInstance);

          const initialAddr = await getCachedOrFetchAddress(
            initialDriverPosition[0],
            initialDriverPosition[1]
          );
          const initialDriverHtml = htmlDriverMarkerContent({
            title: "Motorista (tempo real)",
            lat: initialDriverPosition[0],
            lng: initialDriverPosition[1],
            dateTimeStr: null,
            addressInfo: initialAddr,
          });
          driverMarkerInstance.bindTooltip(initialDriverHtml, tooltipCoordsOptions());
          driverMarkerInstance.bindPopup(initialDriverHtml);

          setDriverMarker(driverMarkerInstance);
        } else {
          console.error("Erro: Nenhuma rota foi retornada pela API.");
        }
      } catch (error) {
        console.error(
          "Erro ao buscar rota:",
          error.response ? error.response.data : error.message
        );
      }
    };

    fetchRoute();

    return () => {
      gpsOverlayGenRef.current += 1;
      mapInstance.remove();
      routeHistoryRef.current = [];
      setMap(null);
      setDriverMarker(null);
    };
  }, [points, idFreight, getCachedOrFetchAddress]);

  useEffect(() => {
    if (!map || !driverMarker || typeof searchLatestPositions !== "function") {
      return undefined;
    }

    const updateDriverPosition = async () => {
      const latestPositions = await searchLatestPositions(lastPositionReceived);
      let newPositions = [];
      let timestamps = [];

      if (latestPositions) {
        latestPositions?.forEach((position) => {
          const latitude = position?.latitude;
          const longitude = position?.longitude;
          const timestamp = position?.timestamp;

          if (latitude !== undefined && longitude !== undefined) {
            newPositions.push([latitude, longitude]);
            timestamps.push(parseInt(timestamp, 10) || null);
          } else {
            console.log(`latitude ou longitude inválida: ,
                latitude = ${position?.latitude},
                longitude = ${position?.longitude},
                timestamp = ${position?.timestamp},
                `);
          }
        });

        if (
          latestPositions?.[latestPositions?.length - 1]?.id !=
            lastPositionReceived &&
          latestPositions?.[latestPositions?.length - 1]?.id
        ) {
          console.log(
            "era: ",
            lastPositionReceived,
            " e trocou para: ",
            latestPositions?.[latestPositions?.length - 1]?.id
          );
          setLastPositionReceived(
            latestPositions?.[latestPositions?.length - 1]?.id
          );
        }
      } else {
        console.log("latestPositions é inválido: ", latestPositions);
      }

      const stoppings = await freightService.searchCurrentWay(idFreight);
      const currentPosData = stoppings.docs[0]?.data() || {};
      const newPosition = [
        currentPosData.latitude || 0,
        currentPosData.longitude || 0,
      ];
      const driverPosTime = toLocaleDateTimeString(currentPosData.timestamp);

      if (newPosition[0] !== 0 && newPosition[1] !== 0) {
        driverMarker.setLatLng(newPosition);
        const addr = await getCachedOrFetchAddress(newPosition[0], newPosition[1]);
        const html = htmlDriverMarkerContent({
          title: "Motorista (tempo real)",
          lat: newPosition[0],
          lng: newPosition[1],
          dateTimeStr: driverPosTime,
          addressInfo: addr,
        });
        const tip = driverMarker.getTooltip();
        if (tip) {
          tip.setContent(html);
        } else {
          driverMarker.bindTooltip(html, tooltipCoordsOptions());
        }
        const popup = driverMarker.getPopup();
        if (popup) {
          popup.setContent(html);
        } else {
          driverMarker.bindPopup(html);
        }
        console.log("New position é válida");
      } else {
        console.log("New position inválida");
      }

      console.log("newPositions: ", newPositions);
      newPositions.forEach((position, idx) => {
        const [lat, lng] = position;
        const timestamp = timestamps[idx] || null;
        const key = `${lat}|${lng}|${timestamp || "sem-ts"}`;
        const exists = routeHistoryRef.current.some(
          (item) => `${item.lat}|${item.lng}|${item.timestamp || "sem-ts"}` === key
        );
        if (!exists) {
          routeHistoryRef.current.push({ lat, lng, timestamp });
        }
      });

      const updatedRoute = [...routeHistoryRef.current];

      const overlayGen = ++gpsOverlayGenRef.current;
      const gpsPointsHtml = await Promise.all(
        updatedRoute.map(async (position) => {
          const pLat = position.lat;
          const pLng = position.lng;
          const dateTimeStr =
            toLocaleDateTimeString(position.timestamp) || "—";
          const addr = await getCachedOrFetchAddress(pLat, pLng);
          const captureHtml = htmlDriverMarkerContent({
            title: "Captura GPS",
            lat: pLat,
            lng: pLng,
            dateTimeStr,
            addressInfo: addr,
          });
          return { pLat, pLng, captureHtml };
        })
      );

      if (overlayGen !== gpsOverlayGenRef.current) {
        return;
      }

      removeGpsOverlayLayers(map);

      gpsPointsHtml.forEach(({ pLat, pLng, captureHtml }) => {
        L.circleMarker([pLat, pLng], {
          className: "mapa-comp-gps-point",
          radius: 5,
          color: "#ffffff",
          weight: 2,
          fillColor: FORTIO_ORANGE,
          fillOpacity: 1,
        })
          .addTo(map)
          .bindPopup(captureHtml)
          .bindTooltip(captureHtml, tooltipCoordsOptions());
      });

      const drawGpsPolyline = !isFreightInTransit(freightStatusDescribe);
      if (drawGpsPolyline && updatedRoute.length > 1) {
        L.polyline(
          updatedRoute.map((item) => [item.lat, item.lng]),
          {
          className: "mapa-comp-gps-line",
          color: "#e53935",
          weight: 4,
          opacity: 0.92,
          lineJoin: "round",
          lineCap: "round",
          }
        ).addTo(map);
      }
    };

    const interval = setInterval(updateDriverPosition, 10000);
    return () => clearInterval(interval);
  }, [
    map,
    driverMarker,
    lastPositionReceived,
    idFreight,
    searchLatestPositions,
    freightStatusDescribe,
    getCachedOrFetchAddress,
  ]);

  return (
    <div
      ref={mapContainerRef}
      className="mapa-comp-wrap"
      style={{ height: mapHeight }}
    />
  );
};

export default MapComponent;
